<template>
  <div class="detail view">
    <template v-if="converter">
      <title-bar class="add-button-padding" :with-logo="false" :title="translateConverter(converter)"/>
      <div id="detail_view_container" style="top: 50px;" class="static">
        <div class="container">
          <div class="row">
            <div class="hidden-lg col-xs-12 align-right">
              <button type="button" id="close" @click="$router.back()">
                <img src="@/img/schliessen_button_grau.png" alt="x">
              </button>
            </div>
            <div class="visible-lg col-xs-12 text-left spacer-bottom">
              <button type="button"
                      style="display: inline-block; width: auto; background: none"
                      @click="$router.back()">
                <i class="fas fa-chevron-left"></i> {{ _('Zurück') }}
              </button>
            </div>
          </div>
          <div class="row detail-content bottom-nav-spacing">
            <div class="col-xs-12 col-lg-5">
              <canvas-carousel
                  v-if="converter.images.length > 0"
                  :images="converter.images"
                  :watermark="watermark"
                  @process-img="forwardProcessImgEvent"
              ></canvas-carousel>
            </div>
            <div class="col-xs-12 col-lg-7 detail-props spacer-bottom">

              <div class="row">
                <div class="col-xs-6">
                  <b>{{ _('Erkennungsmerkmal:') }}</b>
                </div>
                <div class="col-xs-6">
                  {{ identifyingInscriptionString(converter.inscriptions) }}
                </div>
                <div class="col-xs-12">
                  <hr class="spacer-top">
                </div>
              </div>
              <div class="row highlight" v-if="converter.categories && converter.categories.length > 0">
                <div class="col-xs-6">
                  <b>{{ _('Kategorie:') }} </b>
                </div>
                <div class="col-xs-6">
                  {{ priceGroupString(converter.categories) }}
                </div>
                <div class="col-xs-12">
                  <hr class="spacer-top">
                </div>
              </div>
              <div class="row highlight" v-if="converter.prices && converter.prices.length > 0">
                <div class="col-xs-6">
                  <b>{{ _('Wert:') }} </b>
                </div>
                <div class="col-xs-6">
                  <template v-if="converter.prices">
                    <money-format
                        v-for="(price, index) in converter.prices"
                        :key="converter.id + '-' + index"
                        style="display: inline-block"
                        :value="convertConverterPriceToLocal(price)"
                        :locale='userLocal'
                        :currency-code='userCurrency.code'
                        :subunit-value=true
                        :hide-subunits=false>
                    </money-format>
                  </template>
                </div>
                <div class="col-xs-12">
                  <hr class="spacer-top">
                </div>
              </div>
              <div class="row">
                <div class="col-xs-6">
                  <b>{{ _('Material:') }} </b>
                </div>
                <div class="col-xs-6">
                  {{ translateMaterial(converter.material) }}
                </div>
                <div class="col-xs-12">
                  <hr class="spacer-top">
                </div>
              </div>
              <div class="row" v-if="converter.region.business_case_id === 1">
                <div class="col-xs-6">
                  <b>{{ _('Kraftstoff:') }} </b>
                </div>
                <div class="col-xs-6">
                  {{ translateEngine(converter.engine) }}
                </div>
                <div class="col-xs-12">
                  <hr class="spacer-top">
                </div>
              </div>
              <div class="row" v-if="converter.region.business_case_id === 1">
                <div class="col-xs-6">
                  <b>{{ _('Länge:') }} </b>
                </div>
                <div class="col-xs-6">
                  <template v-if="converter.length">{{ converter.length + ' ' + _('cm') }}</template>
                  <template v-else>{{ _('N/A') }}</template>
                </div>
                <div class="col-xs-12">
                  <hr class="spacer-top">
                </div>
              </div>
              <div class="row " v-if="converter.weight">
                <div class="col-xs-6">
                  <b>{{ _('Gewicht:') }} </b>
                </div>
                <div class="col-xs-6">
                  {{ formattedWeight }}
                </div>
                <div class="col-xs-12">
                  <hr class="spacer-top">
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <div v-else class="text-center" style="margin-top: 2em;">
      <img src="@/img/hensel-loading.svg" alt="loading">
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex'
import forwardEvents from '@/mixins/forwardEvents.js'
import TitleBar from '@/components/TitleBar'
import CanvasCarousel from '@/components/CanvasCarousel'
import MoneyFormat from 'vue-money-format'

export default {
  name: 'Details',
  mixins: [forwardEvents],
  components: { TitleBar, CanvasCarousel, MoneyFormat },
  computed: {
    ...mapState({
      user: state => state.Api.Users.current,
      apiVersion: state => state.apiVersion,
      attributes: state => state.Api.Converters.attributes,
      converter: state => state.Api.Converters.current,
    }),
    watermark () {
      if (!this.user) {
        return null
      }

      return this.user.watermark
    },
    currencies () {
      if (!this.attributes) {
        return []
      }

      return this.attributes.currencies
    },
    userCurrency () {
      if (!this.currencies || !this.user) {
        return { code: 'EUR' }
      }

      const currencyId = this.user?.currency_id
      return this.currencies.find(el => el.id === currencyId) || { code: 'EUR' }
    },
    userLocal () {
      return (this.user?.culture_name ?? 'en').split('-').shift()
    },
    formattedWeight() {
      if (!this.converter?.weight) {
        return ''
      }

      const numberFormatter = new Intl.NumberFormat(this.userLocal, {
        style: 'unit',
        unit: 'kilogram'
      })

      return numberFormatter.format(this.converter.weight)
    },
  },
  methods: {
    ...mapActions({
      getConverter: 'Api/Converters/get',
      getUserInfo: 'Api/Users/info',
    }),
    ...mapMutations({
      setConverter: 'Api/Converters/setCurrent',
    }),
    convertConverterPriceToLocal (price) {
      if (!price) {
        return ''
      }

      let value = price.value

      if (this.currencies && price.currency_id !== this.user?.currency_id) {
        const fromCurrency = this.currencies.find(el => {
          return el.id === price.currency_id
        })

        value /= fromCurrency.value
        value *= this.userCurrency.value
      }

      return value
    },
    identifyingInscriptionString (inscriptions) {
      if (!inscriptions) {
        return ''
      }

      return inscriptions
          .filter(inscription => inscription.identifying)
          .map(inscription => this.translateInscription(inscription))
          .join(', ') || this._('N/A')
    },
    priceGroupString (groups) {
      if (!groups) {
        return ''
      }

      return groups
          .filter(group => group.price_group)
          .map(group => group.price_group)
          .join(', ') || this._('N/A')
    },
  },
  mounted () {
    if (!this.user) {
      this.$router.push({ name: 'Login' })
      return;
    }

    if (this.converter && this.converter.id === parseInt(this.$route.params.id)) {
      return
    } else {
      this.setConverter({ item: null })
    }

    this.getConverter({ id: this.$route.params.id })
        .then(this.getUserInfo)
        .catch(this.apiErrorHandler)

  },
}
</script>
